import { useRef, useState } from 'react';
import type { RefObject } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Eyebrow from './Eyebrow';
import Squiggle from './Squiggle';
import { trackStandard, trackCustom, getUtmParams } from '../../lib/pixel';
import { trackPostHog } from '../../lib/analytics';

type Niveau = 'Maternelle' | 'Primaire';

interface FormState {
  prenomEnfant: string;
  niveau: Niveau | '';
  parentName: string;
  phone: string;
  email: string;
}

const INITIAL: FormState = {
  prenomEnfant: '',
  niveau: '',
  parentName: '',
  phone: '',
  email: '',
};

const validatePhone = (phone: string): boolean => {
  const cleaned = phone.replace(/[\s\-().]/g, '');
  return /^(\+212|0)(5|6|7)\d{8}$/.test(cleaned);
};

const validateEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const NIVEAU_AGES: Record<Niveau, string> = {
  Maternelle: '3 – 6 ans',
  Primaire: '6 – 11 ans',
};

const Check = () => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const RsvpForm = () => {
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const formStarted = useRef(false);
  const prenomEnfantRef = useRef<HTMLInputElement>(null);
  const parentNameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const handleFocus = () => {
    if (formStarted.current) return;
    formStarted.current = true;
    trackPostHog('form_started', { page: 'jpo-portes-ouvertes' });
  };

  const validateField = (key: keyof FormState, value: string): string | undefined => {
    if (key === 'prenomEnfant' && value.trim().length > 0 && value.trim().length < 2) {
      return 'Au moins 2 caractères';
    }
    if (key === 'parentName' && value.trim().length > 0 && value.trim().length < 2) {
      return 'Au moins 2 caractères';
    }
    if (key === 'phone' && value.trim().length > 0 && !validatePhone(value)) {
      return 'Numéro marocain invalide (ex. 06 12 34 56 78)';
    }
    if (key === 'email' && value.trim().length > 0 && !validateEmail(value)) {
      return 'Format email invalide';
    }
    return undefined;
  };

  const handleBlur = (fieldName: keyof FormState) => () => {
    const value = form[fieldName];
    const stringValue = typeof value === 'string' ? value : String(value ?? '');
    const inlineError = validateField(fieldName, stringValue);
    if (inlineError) {
      setErrors((prev) => ({ ...prev, [fieldName]: inlineError }));
    }
    trackPostHog('form_field_blur', {
      page: 'jpo-portes-ouvertes',
      field_name: fieldName,
      has_value: stringValue.trim().length > 0,
      has_error: Boolean(inlineError || errors[fieldName]),
    });
  };

  const focusNext = (ref: RefObject<HTMLInputElement>) => {
    setTimeout(() => ref.current?.focus(), 50);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (form.prenomEnfant.trim().length < 2) next.prenomEnfant = 'Au moins 2 caractères';
    if (!form.niveau) next.niveau = 'Choisissez le niveau';
    if (form.parentName.trim().length < 2) next.parentName = 'Au moins 2 caractères';
    if (!form.phone.trim()) next.phone = 'Téléphone requis';
    else if (!validatePhone(form.phone)) next.phone = 'Numéro marocain invalide (ex. 06 12 34 56 78)';
    if (form.email.trim() && !validateEmail(form.email)) next.email = 'Format email invalide';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const emailTrimmed = form.email.trim();
      await axios.post('/api/jpo-rsvp', {
        prenom_enfant: form.prenomEnfant.trim(),
        niveau: form.niveau,
        parent_name: form.parentName.trim(),
        phone: form.phone.trim(),
        ...(emailTrimmed ? { email: emailTrimmed } : {}),
        ...getUtmParams(),
      });

      trackStandard('Lead', { content_name: 'jpo-rsvp', currency: 'MAD' });
      trackCustom('jpo_rsvp_submitted', {
        niveau: form.niveau,
        ...getUtmParams(),
      });
      trackPostHog('form_submitted', {
        page: 'jpo-portes-ouvertes',
        niveau: form.niveau,
        has_email: Boolean(emailTrimmed),
      });

      setIsSubmitted(true);
    } catch (err) {
      console.error('JPO RSVP submission error:', err);
      setSubmitError(
        'Une erreur est survenue. Réessayez ou contactez-nous : tél. 0522908238 ou WhatsApp 0781570080.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section className="rsvp" id="rsvp">
        <div className="container">
          <div className="rsvp-confirm">
            <div className="rsvp-confirm-check" aria-hidden="true">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 className="rsvp-confirm-title">Merci, on vous attend.</h2>
            <p className="rsvp-confirm-body">
              Nous avons bien reçu votre réservation pour <strong>samedi 16 mai, de 9h à 13h</strong>.
              <br />
              À très bientôt à l'école.
            </p>
            <a
              href="https://gssanaa.com"
              target="_blank"
              rel="noopener noreferrer"
              className="rsvp-confirm-link"
            >
              En attendant, découvrez GSS sur gssanaa.com →
            </a>
          </div>
        </div>
      </section>
    );
  }

  const inputCls = (k: keyof FormState) =>
    `field-input${errors[k] ? ' has-error' : ''}`;

  return (
    <section className="rsvp" id="rsvp">
      <div className="container rsvp-container">
        <div className="rsvp-aside">
          <Eyebrow color="var(--gss-yellow-dark)">Réservation</Eyebrow>
          <h2 className="rsvp-title">
            Réservez en
            <br />
            <span className="rsvp-title-emph">
              30 secondes.
              <Squiggle className="title-squiggle" color="var(--gss-yellow)" />
            </span>
          </h2>
          <p className="rsvp-aside-body">
            5 champs, c'est tout. Nous recevons votre réservation immédiatement
            et nous vous attendons sur le campus.
          </p>
          <ul className="rsvp-checklist">
            <li><Check /> Gratuit, sans engagement</li>
            <li><Check /> Test de positionnement offert (sur RDV)</li>
            <li><Check /> Pour Maternelle &amp; Primaire</li>
          </ul>
        </div>

        <form className="rsvp-form" onSubmit={handleSubmit} noValidate>
          {submitError && (
            <p className="rsvp-error" role="alert">
              {submitError}
            </p>
          )}

          <div className="field">
            <span className="field-label">Pour quel niveau ?</span>
            <div className="niveau-grid">
              {(['Maternelle', 'Primaire'] as Niveau[]).map((n) => {
                const active = form.niveau === n;
                return (
                  <button
                    type="button"
                    key={n}
                    className={`niveau-btn${active ? ' is-active' : ''}`}
                    onClick={() => {
                      handleFocus();
                      setField('niveau', n);
                      trackPostHog('form_field_blur', {
                        page: 'jpo-portes-ouvertes',
                        field_name: 'niveau',
                        has_value: true,
                        has_error: false,
                      });
                      focusNext(prenomEnfantRef);
                    }}
                    aria-pressed={active}
                  >
                    <span className="niveau-btn-label">{n}</span>
                    <span className="niveau-btn-age">{NIVEAU_AGES[n]}</span>
                    <span className="niveau-btn-check" aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                  </button>
                );
              })}
            </div>
            {errors.niveau && <p className="field-error">{errors.niveau}</p>}
          </div>

          <div className="field">
            <label htmlFor="prenomEnfant" className="field-label">
              Prénom de votre enfant
            </label>
            <input
              ref={prenomEnfantRef}
              id="prenomEnfant"
              type="text"
              value={form.prenomEnfant}
              onChange={(e) => setField('prenomEnfant', e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur('prenomEnfant')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  parentNameRef.current?.focus();
                }
              }}
              placeholder="Adam"
              className={inputCls('prenomEnfant')}
              autoComplete="given-name"
              enterKeyHint="next"
            />
            {errors.prenomEnfant && (
              <p className="field-error">{errors.prenomEnfant}</p>
            )}
          </div>

          <div className="field">
            <label htmlFor="parentName" className="field-label">
              Votre nom
            </label>
            <input
              ref={parentNameRef}
              id="parentName"
              type="text"
              value={form.parentName}
              onChange={(e) => setField('parentName', e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur('parentName')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  phoneRef.current?.focus();
                }
              }}
              placeholder="Mohamed Alami"
              className={inputCls('parentName')}
              autoComplete="name"
              enterKeyHint="next"
            />
            {errors.parentName && (
              <p className="field-error">{errors.parentName}</p>
            )}
          </div>

          <div className="field-row">
            <div className="field">
              <label htmlFor="phone" className="field-label">
                Téléphone
              </label>
              <input
                ref={phoneRef}
                id="phone"
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur('phone')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    emailRef.current?.focus();
                  }
                }}
                placeholder="06 12 34 56 78"
                className={inputCls('phone')}
                autoComplete="tel"
                enterKeyHint="next"
              />
              {errors.phone && <p className="field-error">{errors.phone}</p>}
            </div>
            <div className="field">
              <label htmlFor="email" className="field-label">
                Email <span className="field-label-optional">(optionnel)</span>
              </label>
              <input
                ref={emailRef}
                id="email"
                type="email"
                inputMode="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                onFocus={handleFocus}
                onBlur={handleBlur('email')}
                placeholder="parent@email.com"
                className={inputCls('email')}
                autoComplete="email"
                enterKeyHint="done"
              />
              {errors.email && <p className="field-error">{errors.email}</p>}
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary btn-lg btn-block"
          >
            {isSubmitting ? (
              <>
                <span className="spinner" aria-hidden="true" />
                Réservation en cours…
              </>
            ) : (
              <>
                Réserver ma place
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </>
            )}
          </button>

          <p className="rsvp-legal">
            En réservant, vous acceptez d'être recontacté par GSS pour confirmer votre venue.
            Voir notre{' '}
            <Link to="/politique-confidentialite">politique de confidentialité</Link>.
          </p>
        </form>
      </div>
    </section>
  );
};

export default RsvpForm;
